import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ButtonComponent, CardComponent, InputComponent, ToasterService } from 'shared';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';

import { AuthService } from '../../../core/auth/auth.service';

@Component({
  selector: 'app-login-page',
  imports: [
    CardComponent,
    InputComponent,
    TranslateModule,
    ButtonComponent,
    RouterLink,
    ReactiveFormsModule,
  ],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss',
})
export class LoginPageComponent implements AfterViewInit, OnDestroy {
  @ViewChild('emailInput', { static: false }) emailInput!: InputComponent;
  @ViewChild('passwordInput', { static: false }) passwordInput!: InputComponent;
  @ViewChild('codeInput1') codeInput1!: ElementRef;
  @ViewChild('codeInput2') codeInput2!: ElementRef;
  @ViewChild('codeInput3') codeInput3!: ElementRef;
  @ViewChild('codeInput4') codeInput4!: ElementRef;
  @ViewChild('codeInput5') codeInput5!: ElementRef;
  @ViewChild('codeInput6') codeInput6!: ElementRef;

  form: FormGroup;
  codeForm: FormGroup;
  isLoading = false;
  private shouldFocusPassword = false;
  private readonly REMEMBER_EMAIL_KEY = 'login-remembered-email-gba';
  private destroy$ = new Subject<void>();

  private authService = inject(AuthService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private toasterService = inject(ToasterService);
  private translate = inject(TranslateService);

  newTouAvailable = false;
  newTouContent: Term[] = [];
  private _view: 'request-email' | 'check-email' = 'request-email';
  showPassword = true;

  get view(): 'request-email' | 'check-email' {
    return this._view;
  }

  set view(value: 'request-email' | 'check-email') {
    this._view = value;

    // Handle view change to check-email
    if (value === 'check-email') {
      // Reset code form
      this.codeForm.reset();

      // Focus first input after a small delay to ensure the DOM is updated
      setTimeout(() => {
        if (this.codeInput1) {
          this.codeInput1.nativeElement.focus();
        }
      }, 100);
    }
  }

  constructor() {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });

    this.codeForm = this.fb.group({
      code1: ['', [Validators.required, Validators.maxLength(1)]],
      code2: ['', [Validators.required, Validators.maxLength(1)]],
      code3: ['', [Validators.required, Validators.maxLength(1)]],
      code4: ['', [Validators.required, Validators.maxLength(1)]],
      code5: ['', [Validators.required, Validators.maxLength(1)]],
      code6: ['', [Validators.required, Validators.maxLength(1)]],
    });

    // Check for navigation state (email from password reset)
    const state = history.state;
    if (state && state.email) {
      this.form.patchValue({ email: state.email });
      this.shouldFocusPassword = true;
      delete state.email;
    } else {
      // Load remembered email from localStorage
      const rememberedEmail = localStorage.getItem(this.REMEMBER_EMAIL_KEY);
      if (rememberedEmail) {
        this.form.patchValue({ email: rememberedEmail });
      }
    }
  }

  ngAfterViewInit(): void {
    // Focus password field if email was prefilled
    const emailValue = this.form.get('email')?.value;

    if (emailValue && emailValue.trim().length > 0) {
      // Email is prefilled, focus password
      setTimeout(() => {
        if (this.passwordInput) {
          this.passwordInput.focus();
        }
      }, 0);
    } else {
      // No email, focus email field
      setTimeout(() => {
        if (this.emailInput) {
          this.emailInput.focus();
        }
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private saveEmailToLocalStorage(email: string): void {
    localStorage.setItem(this.REMEMBER_EMAIL_KEY, email);
  }

  onForgotPasswordClick(): void {
    // Save current email to localStorage before navigating to forgot password page
    const currentEmail = this.form.get('email')?.value;
    if (currentEmail && this.form.get('email')?.valid) {
      this.saveEmailToLocalStorage(currentEmail);
    }
  }

  async onSubmit(): Promise<void> {
    if (this.form.get('email')?.invalid) return;
    this.isLoading = true;

    if (this.view === 'request-email' && !this.showPassword && this.newTouAvailable === false) {
      /*
      this.authService.requestLoginEmail(this.form.value.email).subscribe({
        next: (response) => {
          console.log('Request login email response', response);
          this.isLoading = false;
          this.view = 'check-email';
          this.saveEmailToLocalStorage(this.form.value.email);
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading = false;
          if (err.status === 400) {
            this.toasterService.error({
              message: this.translate.instant('no-account-found-for-email'),
            });
            this.form.get('email')?.setValue('');
            if (this.emailInput) {
              this.emailInput.focus();
            }
          } else {
            this.toasterService.error({
              message: this.translate.instant('something-went-wrong'),
            });
            console.log('Request login email failed', err);
          }
        },
          
      });
        */
    } else if (
      this.view === 'request-email' &&
      this.showPassword &&
      this.newTouAvailable === false
    ) {
      if (this.form.get('password')?.invalid) {
        this.isLoading = false;
        return;
      }

      this.authService
        .login(this.form.value.email, this.form.value.password)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.isLoading = false;
            // Save email to localStorage on successful login
            this.saveEmailToLocalStorage(this.form.value.email);
            // Navigate after successful login
            const redirect = this.authService.redirectUrl || '/companies';
            this.router.navigateByUrl(redirect);
            this.authService.redirectUrl = null;
          },
          error: (err: HttpErrorResponse) => {
            this.isLoading = false;
            if (err.status === 401) {
              this.toasterService.error({
                message: this.translate.instant('wrong-username-or-password'),
              });
            } else if (err.status === 403 && err.error.message === 'New Terms and Conditions') {
              this.toasterService.info({
                title: err.error.message,
                message: this.translate.instant('accept-new-terms-and-conditions-to-continue'),
                duration: 10000,
              });
              this.newTouAvailable = true;
              this.newTouContent = err.error.terms as Term[];
            } else {
              this.toasterService.error({
                message: this.translate.instant('something-went-wrong'),
              });
              console.log('Login failed', err);
            }
          },
        });
    } else {
      if (this.showPassword) {
        // Use password login with ToU acceptance
        this.authService
          .login(this.form.value.email, this.form.value.password)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isLoading = false;
              // Save email to localStorage on successful login
              this.saveEmailToLocalStorage(this.form.value.email);
              // Navigate after successful login
              const redirect = this.authService.redirectUrl || '/companies';
              this.router.navigateByUrl(redirect);
              this.authService.redirectUrl = null;
            },
            error: (err: HttpErrorResponse) => {
              this.isLoading = false;
              if (err.status === 401) {
                this.toasterService.error({
                  message: this.translate.instant('wrong-username-or-password'),
                });
              } else if (err.status === 403 && err.error.message === 'New Terms and Conditions') {
                this.toasterService.info({
                  title: err.error.message,
                  message: this.translate.instant('accept-new-terms-and-conditions-to-continue'),
                  duration: 10000,
                });
                this.newTouAvailable = true;
                this.newTouContent = err.error.terms as Term[];
              } else {
                this.toasterService.error({
                  message: this.translate.instant('something-went-wrong'),
                });
                console.log('Login failed', err);
              }
            },
          });
      } else {
        await this.onSubmitCode();
      }
    }
  }

  async onSubmitCode(): Promise<void> {
    /*
    if (this.codeForm.invalid) return;

    this.isLoading = true;

    // Combine the verification code and log for now
    const code =
      this.codeForm.get('code1')?.value +
      this.codeForm.get('code2')?.value +
      this.codeForm.get('code3')?.value +
      this.codeForm.get('code4')?.value +
      this.codeForm.get('code5')?.value +
      this.codeForm.get('code6')?.value;

    const termIds = this.newTouContent.map((term) => term._id);
    this.authService
      .verifyLoginCode(this.form.get('email')?.value, code, termIds)
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.saveEmailToLocalStorage(this.form.get('email')?.value);
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading = false;
          if (err.status === 401) {
            this.toasterService.error({
              message: this.translate.instant('invalid-verification-code'),
            });
          } else if (err.status === 403 && err.error.message === 'New Terms and Conditions') {
            this.toasterService.info({
              title: err.error.message,
              message: this.translate.instant('accept-new-terms-and-conditions-to-continue'),
              duration: 10000,
            });
            this.newTouAvailable = true;
            this.newTouContent = err.error.terms as Term[];
          } else {
            this.toasterService.error({
              message: this.translate.instant('something-went-wrong'),
            });
            console.log('Verify login code failed', err);
          }
        },
      });
    */
  }

  requestNewCode(): void {
    /*
    this.authService.requestLoginEmail(this.form.get('email')?.value).subscribe({
      next: () => {
        this.toasterService.info({
          message: this.translate.instant('new-login-code-sent'),
        });
        if (this.codeInput1) {
          this.codeInput1.nativeElement.focus();
        }
      },
      error: (err) => {
        this.toasterService.error({
          message: this.translate.instant('something-went-wrong'),
        });
        console.error('Request new code failed', err);
      },
    });
    */
    // For now, just inform the user that this is not implemented
    this.toasterService.info({
      message: this.translate.instant('request-new-code-not-implemented'),
    });
  }

  onCodeInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;

    // Handle multi-digit input (could happen with autocomplete or paste)
    if (input.value.length > 1) {
      // Get the full value and clean it (only digits)
      const fullValue = input.value.replace(/\D/g, '');

      // Distribute digits to all inputs starting from the current index
      for (let i = 0; i < Math.min(6 - index + 1, fullValue.length); i++) {
        const targetIndex = index + i;
        const controlName = `code${targetIndex}`;

        if (this.codeForm.get(controlName) && fullValue[i]) {
          this.codeForm.get(controlName)?.setValue(fullValue[i]);
        }
      }

      // Focus the next empty input or the last filled input
      const nextFocusIndex = Math.min(6, index + fullValue.length);
      let nextInput: ElementRef | null = null;

      switch (nextFocusIndex) {
        case 1:
          nextInput = this.codeInput1;
          break;
        case 2:
          nextInput = this.codeInput2;
          break;
        case 3:
          nextInput = this.codeInput3;
          break;
        case 4:
          nextInput = this.codeInput4;
          break;
        case 5:
          nextInput = this.codeInput5;
          break;
        case 6:
          nextInput = this.codeInput6;
          break;
      }

      if (nextInput) {
        nextInput.nativeElement.focus();
      }

      // Auto-submit if all 6 digits are filled and form is valid
      if (fullValue.length >= 6 - index + 1 && this.codeForm.valid) {
        this.onSubmitCode();
      }

      return;
    }

    // Handle single character input (existing logic)
    if (input.value.length === 1 && index < 6) {
      // Move to next input
      if (index === 1 && this.codeInput2) this.codeInput2.nativeElement.focus();
      else if (index === 2 && this.codeInput3) this.codeInput3.nativeElement.focus();
      else if (index === 3 && this.codeInput4) this.codeInput4.nativeElement.focus();
      else if (index === 4 && this.codeInput5) this.codeInput5.nativeElement.focus();
      else if (index === 5 && this.codeInput6) this.codeInput6.nativeElement.focus();
    } else if (input.value.length === 0 && index > 1) {
      // Move to previous input on backspace
      if (index === 2 && this.codeInput1) this.codeInput1.nativeElement.focus();
      else if (index === 3 && this.codeInput2) this.codeInput2.nativeElement.focus();
      else if (index === 4 && this.codeInput3) this.codeInput3.nativeElement.focus();
      else if (index === 5 && this.codeInput4) this.codeInput4.nativeElement.focus();
      else if (index === 6 && this.codeInput5) this.codeInput5.nativeElement.focus();
    }

    // Auto-submit if the last digit (index 6) was entered and the form is valid
    if (index === 6 && input.value.length === 1 && this.codeForm.valid) {
      this.onSubmitCode();
    }
  }

  onKeyDown(index: number, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    // Handle digit keys when input is already filled
    if (/^\d$/.test(event.key) && input.value.length === 1 && index < 6) {
      // If current input is already filled and user types a digit, prevent default
      // and add that digit to the next input instead
      event.preventDefault();

      let nextInput: ElementRef | null = null;
      let nextFormControl = '';

      if (index === 1 && this.codeInput2) {
        nextInput = this.codeInput2;
        nextFormControl = 'code2';
      } else if (index === 2 && this.codeInput3) {
        nextInput = this.codeInput3;
        nextFormControl = 'code3';
      } else if (index === 3 && this.codeInput4) {
        nextInput = this.codeInput4;
        nextFormControl = 'code4';
      } else if (index === 4 && this.codeInput5) {
        nextInput = this.codeInput5;
        nextFormControl = 'code5';
      } else if (index === 5 && this.codeInput6) {
        nextInput = this.codeInput6;
        nextFormControl = 'code6';
      }

      if (nextInput && nextFormControl) {
        // Set the value of the next input and focus it
        this.codeForm.get(nextFormControl)?.setValue(event.key);
        nextInput.nativeElement.focus();

        // Auto-submit if the last digit was entered and form is valid
        if (index === 5 && this.codeForm.valid) {
          this.onSubmitCode();
        }
      }
    }
    // Handle backspace key
    else if (event.key === 'Backspace') {
      // If input is empty and we're not at the first input, move to previous input
      if (input.value.length === 0 && index > 1) {
        event.preventDefault(); // Prevent default backspace behavior

        let previousInput: ElementRef | null = null;

        if (index === 2 && this.codeInput1) previousInput = this.codeInput1;
        else if (index === 3 && this.codeInput2) previousInput = this.codeInput2;
        else if (index === 4 && this.codeInput3) previousInput = this.codeInput3;
        else if (index === 5 && this.codeInput4) previousInput = this.codeInput4;
        else if (index === 6 && this.codeInput5) previousInput = this.codeInput5;

        if (previousInput) {
          previousInput.nativeElement.focus();
          // Optional: If you want to clear the previous input value
          // this.codeForm.get(`code${index-1}`)?.setValue('');
        }
      }
    }
    // Handle arrow left to navigate to previous input
    else if (event.key === 'ArrowLeft' && index > 1) {
      event.preventDefault();

      let previousInput: ElementRef | null = null;

      if (index === 2 && this.codeInput1) previousInput = this.codeInput1;
      else if (index === 3 && this.codeInput2) previousInput = this.codeInput2;
      else if (index === 4 && this.codeInput3) previousInput = this.codeInput3;
      else if (index === 5 && this.codeInput4) previousInput = this.codeInput4;
      else if (index === 6 && this.codeInput5) previousInput = this.codeInput5;

      if (previousInput) {
        previousInput.nativeElement.focus();
      }
    }
    // Handle arrow right to navigate to next input
    else if (event.key === 'ArrowRight' && index < 6) {
      event.preventDefault();

      let nextInput: ElementRef | null = null;

      if (index === 1 && this.codeInput2) nextInput = this.codeInput2;
      else if (index === 2 && this.codeInput3) nextInput = this.codeInput3;
      else if (index === 3 && this.codeInput4) nextInput = this.codeInput4;
      else if (index === 4 && this.codeInput5) nextInput = this.codeInput5;
      else if (index === 5 && this.codeInput6) nextInput = this.codeInput6;

      if (nextInput) {
        nextInput.nativeElement.focus();
      }
    }
  }

  usePassword() {
    this.view = 'request-email';
    this.showPassword = true;

    setTimeout(() => {
      const emailValue = this.form.get('email')?.value;
      if (emailValue && this.passwordInput) {
        // If email is prefilled, focus password field
        this.passwordInput.focus();
      } else if (this.emailInput) {
        // Otherwise focus email field
        this.emailInput.focus();
      }
    }, 100);
  }

  dontUsePassword() {
    this.view = 'check-email';
    this.showPassword = false;
  }

  handleCodePaste(event: ClipboardEvent): void {
    event.preventDefault();

    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const pastedText = clipboardData.getData('text').replace(/\D/g, '');

    if (!pastedText) return;

    for (let i = 0; i < Math.min(6, pastedText.length); i++) {
      const controlName = `code${i + 1}`;
      if (this.codeForm.get(controlName)) {
        this.codeForm.get(controlName)?.setValue(pastedText[i]);
      }
    }

    const nextIndex = Math.min(6, pastedText.length) + 1;
    if (nextIndex <= 6) {
      let nextInput: ElementRef | null = null;

      switch (nextIndex) {
        case 2:
          nextInput = this.codeInput2;
          break;
        case 3:
          nextInput = this.codeInput3;
          break;
        case 4:
          nextInput = this.codeInput4;
          break;
        case 5:
          nextInput = this.codeInput5;
          break;
        case 6:
          nextInput = this.codeInput6;
          break;
      }

      if (nextInput) {
        nextInput.nativeElement.focus();
      }
    }

    if (pastedText.length >= 6 && this.codeForm.valid) {
      this.onSubmitCode();
    }
  }
}

export type LocalizedStrings = Record<string, string>;

export interface Term {
  _id: string;
  _rev?: string;
  title: LocalizedStrings;
  html_content: LocalizedStrings;
  modified: string;
  active: boolean;
}
